import { updateUserAIV3UsageConsent } from 'api/v2/users/settings';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useCallback } from 'react';
import useMutation from 'hooks/react-query/useMutation';
import useQueryClient from 'hooks/react-query/useClientQuery';

import { UserCacheKeys } from 'types/cacheKeys.types';
import type { AIV3UsageConsent, IUser } from 'types/users.types';
import {
  AIFeatureUsageConsentEnum,
  AIToolUsageConsentEnum,
} from 'types/users.types';

type UseAIUsageConsentProps = {
  onUpdate?: (consents?: AIV3UsageConsent) => void;
};

type UseAIUsageConsentReturn = {
  isLoading?: boolean;
  consents: AIV3UsageConsent;
  patchConsents: (consents: AIV3UsageConsent) => Promise<IUser>;
};

const DEFAULT_CONSENTS = {
  expenses: AIToolUsageConsentEnum.ask,
  revenues: AIToolUsageConsentEnum.ask,
  taxes: AIToolUsageConsentEnum.ask,
  vehicles: AIToolUsageConsentEnum.ask,
  documents_repository: AIToolUsageConsentEnum.ask,
  summaries: AIToolUsageConsentEnum.ask,
  ai_tax_advisor: AIFeatureUsageConsentEnum.deny,
};

const useAIUsageConsent = ({
  onUpdate,
}: UseAIUsageConsentProps = {}): UseAIUsageConsentReturn => {
  const queryClient = useQueryClient();
  const { data: user, isLoading: isLoadingUser } = useGetCurrentCustomerData();

  const onConsentUpdate = useCallback(
    (user: IUser) => {
      queryClient.invalidateQueries(UserCacheKeys.UserData);
      onUpdate?.(user?.aiUsageConsents);
    },
    [queryClient, onUpdate],
  );

  const { mutateAsync: patchConsents, isLoading } = useMutation(
    (consents: AIV3UsageConsent) => updateUserAIV3UsageConsent(consents),
    { onSuccess: onConsentUpdate },
  );

  return {
    isLoading: isLoadingUser || isLoading,
    consents: user?.aiUsageConsents || DEFAULT_CONSENTS,
    patchConsents,
  };
};

export default useAIUsageConsent;
